import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Grid,
  Button,
  TextField,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Box,
  Typography,
  Autocomplete,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import Header from "../../components/Header";
import * as yup from "yup";
import axios from "axios";
import LoadingIndicator from "../../components/loadingIndicator";
import { userRegisterActionFn } from "../../redux/actions/userAuthAction";
import { profession } from "../../constant/filterMenuItems";
import { useGetProfessionQuery } from "../../state/api";

const checkUsernameAvailability = async (value) => {
  try {
    const response = await axios.get(`/user/checkUsername?username=${value}`);
    const exists = response.data.exists;
    return exists;
  } catch (error) {
    console.error(error);
    return false;
  }
};
const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const validationSchema = yup.object().shape({
  username: yup
    .string()
    .required("Username is required")
    .test(
      "valid-characters",
      "Username can only contain letters, numbers, and @,$,-,_",
      function (value) {
        return /^[a-zA-Z0-9@,$\-_]*$/.test(value);
      }
    )
    .test("length", "Username must be between 4 and 12 characters", (value) => {
      return value.length >= 4 && value.length <= 12;
    })
    .test("is-unique", "Username already exists", async function (value) {
      // Check if username already exists
      try {
        const exists = await checkUsernameAvailability(value);
        console.log("Exists:", exists); // Log to check if the value is retrieved correctly
        return !exists; // Resolve to true if the username doesn't exist (unique)
      } catch (error) {
        console.error("Validation Error:", error); // Log any validation errors
        return false; // Return false in case of an error
      }
    }),
  mobileNumber: yup
    .string()
    .matches(/^[0-9]+$/, "Mobile number must contain only digits")
    .length(10, "Mobile number must be exactly 10 digits")
    .required("Mobile number is required"),
  fullname: yup.string().required("Full name is required"),
  email: yup
    .string()
    .matches(emailRegex, "Invalid email format")
    .required("Email is required"),
  password: yup
    .string()
    .min(8, "Password must be at least 8 characters")
    .max(15, "Password must be less than 15 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,12}$/,
      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
    )
    .required("Password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required("Please confirm your password"),
  gender: yup.string().required("Gender is required"),
  // userType: yup.string().required("User Type is required"),
  profilePicture: yup.mixed(),
  dob: yup
    .date()
    .nullable()
    .typeError("Date of birth is required")
    .required("Date of birth is required"),
  addressLine: yup.string().required("Address-Line is required"),
  postCode: yup.string().required("Zip-code is required"),
  idNumber: yup.string().required("Document Id number is required"),
});

function CreateUser() {
  const [err, setErr] = useState({});
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullname: "",
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
    gender: "",
    // userType: "",
    profession: "",
    dob: "",
    mobileNumber: "",
    addressLine: "",
    postCode: "",
    idNumber: "",
    professionId: "",
  });

  const handleBlur = async (e) => {
    const fieldName = e.target.name;
    try {
      await validationSchema.validateAt(fieldName, formData);
      setErr({ ...err, [fieldName]: "" }); // No errors, clear the error for this field
    } catch (error) {
      setErr({ ...err, [fieldName]: error.message }); // Set the error message for this field
    }
  };

  // const handleChange = async (e) => {
  //   const { name, value } = e.target;

  //   setFormData((prevData) => {
  //     let updatedData = {
  //       ...prevData,
  //       [name]: value,
  //     };
  //     if (name === "professionId") {
  //       const selectedProfession = professionData.find(
  //         (profession) => profession._id === value
  //       );
  //       updatedData = {
  //         ...updatedData,
  //         profession: selectedProfession ? selectedProfession.title : "",
  //       };
  //     }
  //     return updatedData;
  //   });
  // };

  const handleChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const [buttonWidth, setButtonWidth] = useState("100%");
  const [profilePicture, setProfilePicture] = useState("");
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [ppSizePhoto, setppSizePhoto] = useState("");
  const [ppSizePhotoPreview, setPpSizePhotoPreview] = useState(null);
  const [idFront, setIdFront] = useState("");
  const [idFrontPreview, setIdFrontPreview] = useState(null);
  const [idBack, setIdBack] = useState("");
  const [idBackPreview, setIdBackPreview] = useState(null);

  const handleProfilePictureChange = (e) => {
    const selectedProfilePicture = e.target.files[0];
    console.log("Selected profile picture:", selectedProfilePicture);
    setProfilePicture(selectedProfilePicture);

    if (selectedProfilePicture) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setProfilePicturePreview(event.target.result);
        setButtonWidth("70%");
      };
      reader.readAsDataURL(selectedProfilePicture);
    } else {
      setProfilePicturePreview(null);
      setButtonWidth("100%");
    }
  };

  const handleppSizePhotoChange = (e) => {
    const selectedPpSizePhoto = e.target.files[0];
    setppSizePhoto(selectedPpSizePhoto);

    if (selectedPpSizePhoto) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPpSizePhotoPreview(event.target.result);
        setButtonWidth("70%");
      };
      reader.readAsDataURL(selectedPpSizePhoto);
    } else {
      setPpSizePhotoPreview(null);
      setButtonWidth("100%");
    }
  };

  const handleIdFrontChange = (e) => {
    const selectedIdFront = e.target.files[0];
    setIdFront(selectedIdFront);

    if (selectedIdFront) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setIdFrontPreview(event.target.result);
        setButtonWidth("70%");
      };
      reader.readAsDataURL(selectedIdFront);
    } else {
      setIdFrontPreview(null);
      setButtonWidth("100%");
    }
  };

  const handleIdBackChange = (e) => {
    const selectedIdBack = e.target.files[0];
    setIdBack(selectedIdBack);

    if (selectedIdBack) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setIdBackPreview(event.target.result);
        setButtonWidth("70%");
      };
      reader.readAsDataURL(selectedIdBack);
    } else {
      setIdBackPreview(null);
      setButtonWidth("100%");
    }
  };

  const [idType, setIdType] = useState("");

  const handleDocumentTypeChange = (event) => {
    setIdType(event.target.value);
  };

  // const [selectedProfessions, setSelectedProfssions] = useState([]);

  // const handleSelectedProfession = (event, value) => {
  //   setSelectedProfssions(value);
  //   const professionLable = value.map((profession) => profession.label);
  //   const updatedFormData = { ...formData, profession: professionLable };
  //   setFormData(updatedFormData);
  // };
  const { data: professionResponse = {} } = useGetProfessionQuery();
  const professionData = professionResponse.professions || [];
  const [selectedProfessions, setSelectedProfessions] = useState([]);

  const handleProfessionChange = (event, newValue) => {
    setSelectedProfessions(newValue);

    const professionTitles = newValue.map((profession) => profession.title);

    setFormData((prevState) => ({
      ...prevState,
      profession: professionTitles,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      // Validate form data
      // await validationSchema.validate(formData, { abortEarly: false });

      const formDataToSend = new FormData();

      Object.keys(formData).forEach((key) => {
        formDataToSend.append(key, formData[key]);
      });
      formDataToSend.append("profilePicture", profilePicture);
      formDataToSend.append("ppSizePhoto", ppSizePhoto);
      formDataToSend.append("idFront", idFront);
      formDataToSend.append("idBack", idBack);
      formDataToSend.append("idType", idType);

      selectedProfessions.forEach((profession) => {
        formDataToSend.append("professions[]", profession.title);
      });

      // Display the key/value pairs
      for (var pair of formDataToSend.entries()) {
        console.log("formAppend", pair[0] + ":" + pair[1]);
      }

      if (profilePicture && profilePicture.size > 2 * 1024 * 1024) {
        setErr({
          ...err,
          profilePicture: "File size is too large (max 2MB)",
        });
        return;
      } else {
        setErr({ ...err, profilePicture: "" });
      }
      const result = await dispatch(userRegisterActionFn(formDataToSend));
      if (result.success) {
        toast.success("Customer created successfully!");
        setTimeout(() => {
          navigate("/user");
        }, 1000);
      } else {
        toast.error(result.message || "Somethings went wrong");
      }
    } catch (validationErrors) {
      console.error("Error occurred while adding user:", validationErrors);
      if (validationErrors.inner) {
        // Yup validation error
        const errors = {};
        validationErrors.inner.forEach((validationError) => {
          errors[validationError.path] = validationError.message;
        });
        setErr(errors);
      } else {
        // Any other unexpected error — show it instead of failing silently
        toast.error(
          validationErrors?.message || "Something went wrong while adding the user"
        );
      }
    } finally {
      setLoading(false); // Stop loading
    }
  };

  return (
    <Box mt="1.5rem" mb="2.5rem" ml="2.5rem" mr="2.5rem">
      <Header title="ADD USER" />
      <form onSubmit={handleSubmit}>
        <FormControl fullWidth variant="outlined" margin="normal">
          <Grid
            container
            spacing={3}
            alignItems="center"
            justifyContent="space-between"
          >
            {/* FIRST NAME */}
            <Grid item sm={12} md={6}>
              <TextField
                label="Full Name"
                name="fullname"
                value={formData.fullname}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
              />
              {err.fullname && (
                <Grid item>
                  <Typography color="error">{err.fullname}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* USER NAME */}
            <Grid item sm={12} md={6}>
              <TextField
                label="User Name"
                name="username"
                value={formData.username}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
              />
              {err.username && (
                <Grid item>
                  <Typography color="error">{err.username}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* PASSWORD */}
            <Grid item sm={12} md={6}>
              <TextField
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
              />
              {err.password && (
                <Grid item>
                  <Typography color="error">{err.password}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* CONFIRM PASSWORD */}
            <Grid item sm={12} md={6}>
              <TextField
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
              />
              {err.confirmPassword && (
                <Grid item>
                  <Typography color="error">{err.confirmPassword}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* EMAIL */}
            <Grid item sm={12} md={6}>
              <TextField
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
              />
              {err.email && (
                <Grid item>
                  <Typography color="error">{err.email}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* CONTACT NUMBER */}
            <Grid item sm={12} md={6}>
              <TextField
                label="Mobile Number"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyPress={(e) => {
                  const onlyNums = e.target.value.replace(/[^0-9]/g, "");
                  if (e.target.value !== onlyNums) {
                    e.target.value = onlyNums;
                  }
                }}
                fullWidth
              />
              {err.mobileNumber && (
                <Grid item>
                  <Typography color="error">{err.mobileNumber}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* DATE OF BIRTH */}
            <Grid container item sm={12} md={5} alignItems="center">
              <Grid item>
                <span style={{ fontSize: "17px" }}>Date of Birth</span>
              </Grid>
              <Grid item style={{ marginLeft: "8px" }}>
                <TextField
                  type="date"
                  name="dob"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                />
                {err.dob && (
                  <Grid item>
                    <Typography color="error">{err.dob}</Typography>
                  </Grid>
                )}{" "}
              </Grid>
            </Grid>

            {/* UPLOAD PROFILE */}
            <Grid item sm={12} md={7}>
              <FormControl fullWidth>
                <label
                  htmlFor="upload-profile-picture"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Button
                    variant="outlined"
                    color="info"
                    component="span"
                    size="large"
                    style={{
                      width: buttonWidth,
                      fontSize: "1rem",
                      textTransform: "none",
                    }}
                  >
                    Upload Profile Picture
                  </Button>
                  <input
                    id="upload-profile-picture"
                    name="profilePicture"
                    type="file"
                    onChange={handleProfilePictureChange}
                    onBlur={handleBlur}
                    accept="image/*"
                    style={{ display: "none" }}
                  />
                  {err.profilePicture && (
                    <Grid item>
                      <Typography color="error">
                        {err.profilePicture}
                      </Typography>
                    </Grid>
                  )}{" "}
                  {profilePicturePreview && (
                    <img
                      src={profilePicturePreview}
                      alt="Profile"
                      style={{
                        maxWidth: "150px",
                        maxHeight: "70px",
                        marginLeft: "10px",
                      }}
                    />
                  )}
                </label>
              </FormControl>
            </Grid>

            {/* GENDER */}
            <Grid item sm={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Others">Others</MenuItem>
                </Select>
              </FormControl>
              {err.gender && (
                <Grid item>
                  <Typography color="error">{err.gender}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* ADDRESS-LINE */}
            <Grid item sm={12} md={8}>
              <TextField
                label="Address-Line"
                name="addressLine"
                value={formData.address}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
              />
              {err.addressLine && (
                <Grid item>
                  <Typography color="error">{err.addressLine}</Typography>
                </Grid>
              )}{" "}
            </Grid>

            {/* POSTAL CODE */}
            <Grid item sm={12} md={4}>
              <FormControl fullWidth>
                <TextField
                  label="Postal/Zip-Code"
                  name="postCode"
                  value={formData.postCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                />
                {err.zipCpostCodeode && (
                  <Grid item>
                    <Typography color="error">{err.postCode}</Typography>
                  </Grid>
                )}
              </FormControl>
            </Grid>

            {/* PROFESSION */}
            <Grid item sm={12} md={8}>
              <FormControl fullWidth>
                <Autocomplete
                  multiple
                  id="profession"
                  options={professionData}
                  getOptionLabel={(option) => option.title}
                  closeOnSelect={false}
                  onChange={handleProfessionChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Profession"
                      placeholder="Select Profession"
                      variant="outlined"
                      fullWidth
                      margin="normal"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            {/* USER TYPE */}
            {/* <Grid item sm={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  name="userType"
                  value={formData.userType}
                  onChange={handleChange}
                  // onBlur={handleBlur}
                >
                  <MenuItem value="Regular">Regular</MenuItem>
                  <MenuItem value="Celebrity">Celebrity</MenuItem>
                </Select>
              </FormControl>
              {err.gender && (
                <Grid item>
                  <Typography color="error">{err.gender}</Typography>
                </Grid>
              )}{" "}
            </Grid> */}
            <Grid item md={12} mb={2}>
              <h3 style={{ textAlign: "center" }}>
                FIELD FOR KYC VERIFICATION
              </h3>
            </Grid>

            {/* kyc fields */}
            <Grid container spacing={2}>
              <Grid container justifyContent="center" alignItems="center">
                <Grid item md={6} sm={12}>
                  <FormControl fullWidth>
                    <Select
                      value={idType}
                      onChange={handleDocumentTypeChange}
                      displayEmpty
                    >
                      <MenuItem value="">Select Document Type</MenuItem>
                      <MenuItem value="citizenship">Citizenship</MenuItem>
                      <MenuItem value="license">License</MenuItem>
                      <MenuItem value="passport">Passport</MenuItem>
                      <MenuItem value="pancard">Pancard</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {idType === "citizenship" && (
                <>
                  <Grid item sm={12} md={6}>
                    <TextField
                      label="Citizenship Number"
                      name="idNumber"
                      type="text"
                      value={formData.idNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                    />
                  </Grid>

                  {/* PP-Size photo */}
                  <Grid item sm={12} md={6}>
                    <FormControl fullWidth>
                      <label
                        htmlFor="ppSizePhoto"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <Button
                          variant="outlined"
                          color="info"
                          component="span"
                          size="large"
                          style={{
                            width: buttonWidth,
                            fontSize: "1rem",
                            textTransform: "none",
                          }}
                        >
                          Upload pp-size photo
                        </Button>
                        <input
                          id="ppSizePhoto"
                          name="ppSizePhoto"
                          type="file"
                          onChange={handleppSizePhotoChange}
                          accept="image/*"
                          style={{ display: "none" }}
                        />
                        {ppSizePhotoPreview && (
                          <img
                            src={ppSizePhotoPreview}
                            alt="idFront"
                            style={{
                              maxWidth: "150px",
                              maxHeight: "70px",
                              marginLeft: "10px",
                            }}
                          />
                        )}
                      </label>
                    </FormControl>
                  </Grid>
                  {/* Upload citizenship Front */}
                  <Grid item sm={12} md={6}>
                    <FormControl fullWidth>
                      <label
                        htmlFor="idFront"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <Button
                          variant="outlined"
                          color="info"
                          component="span"
                          size="large"
                          style={{
                            width: buttonWidth,
                            fontSize: "1rem",
                            textTransform: "none",
                          }}
                        >
                          Upload Citizenship Front
                        </Button>
                        <input
                          id="idFront"
                          name="idFront"
                          type="file"
                          onChange={handleIdFrontChange}
                          accept="image/*"
                          style={{ display: "none" }}
                        />
                        {idFrontPreview && (
                          <img
                            src={idFrontPreview}
                            alt="idFront"
                            style={{
                              maxWidth: "150px",
                              maxHeight: "70px",
                              marginLeft: "10px",
                            }}
                          />
                        )}
                      </label>
                    </FormControl>
                  </Grid>
                  <Grid item sm={12} md={6}>
                    {/* Upload Citizenship Back */}{" "}
                    <FormControl fullWidth>
                      <label
                        htmlFor="idBack"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <Button
                          variant="outlined"
                          color="info"
                          component="span"
                          size="large"
                          style={{
                            width: buttonWidth,
                            fontSize: "1rem",
                            textTransform: "none",
                          }}
                        >
                          Upload Citizenship Back
                        </Button>
                        <input
                          id="idBack"
                          name="idBack"
                          type="file"
                          onChange={handleIdBackChange}
                          accept="image/*"
                          style={{ display: "none" }}
                        />
                        {idBackPreview && (
                          <img
                            src={idBackPreview}
                            alt="idBack"
                            style={{
                              maxWidth: "150px",
                              maxHeight: "70px",
                              marginLeft: "10px",
                            }}
                          />
                        )}
                      </label>
                    </FormControl>
                  </Grid>
                </>
              )}

              {idType === "license" && (
                <>
                  <Grid item sm={12} md={6}>
                    <TextField
                      label="License Number"
                      name="idNumber"
                      type="text"
                      value={formData.idNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                    />
                  </Grid>

                  {/* PP-Size photo */}
                  <Grid item sm={12} md={6}>
                    <FormControl fullWidth>
                      <label
                        htmlFor="ppSizePhoto"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <Button
                          variant="outlined"
                          color="info"
                          component="span"
                          size="large"
                          style={{
                            width: buttonWidth,
                            fontSize: "1rem",
                            textTransform: "none",
                          }}
                        >
                          Upload pp-size photo
                        </Button>
                        <input
                          id="ppSizePhoto"
                          name="ppSizePhoto"
                          type="file"
                          onChange={handleppSizePhotoChange}
                          accept="image/*"
                          style={{ display: "none" }}
                        />
                        {ppSizePhotoPreview && (
                          <img
                            src={ppSizePhotoPreview}
                            alt="idFront"
                            style={{
                              maxWidth: "150px",
                              maxHeight: "70px",
                              marginLeft: "10px",
                            }}
                          />
                        )}
                      </label>
                    </FormControl>
                  </Grid>

                  {/* Upload License Front */}
                  <Grid item sm={12} md={6}>
                    <FormControl fullWidth>
                      <label
                        htmlFor="idFront"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <Button
                          variant="outlined"
                          color="info"
                          component="span"
                          size="large"
                          style={{
                            width: buttonWidth,
                            fontSize: "1rem",
                            textTransform: "none",
                          }}
                        >
                          Upload License Front
                        </Button>
                        <input
                          id="idFront"
                          name="idFront"
                          type="file"
                          onChange={handleIdFrontChange}
                          accept="image/*"
                          style={{ display: "none" }}
                        />
                        {idFrontPreview && (
                          <img
                            src={idFrontPreview}
                            alt="idFront"
                            style={{
                              maxWidth: "150px",
                              maxHeight: "70px",
                              marginLeft: "10px",
                            }}
                          />
                        )}
                      </label>
                    </FormControl>
                  </Grid>
                </>
              )}

              {idType === "passport" && (
                <>
                  <Grid item sm={12} md={6}>
                    <TextField
                      label="Passport Number"
                      name="idNumber"
                      type="text"
                      value={formData.idNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                    />
                  </Grid>

                  {/* PP-Size photo */}
                  <Grid item sm={12} md={6}>
                    <FormControl fullWidth>
                      <label
                        htmlFor="ppSizePhoto"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <Button
                          variant="outlined"
                          color="info"
                          component="span"
                          size="large"
                          style={{
                            width: buttonWidth,
                            fontSize: "1rem",
                            textTransform: "none",
                          }}
                        >
                          Upload pp-size photo
                        </Button>
                        <input
                          id="ppSizePhoto"
                          name="ppSizePhoto"
                          type="file"
                          onChange={handleppSizePhotoChange}
                          accept="image/*"
                          style={{ display: "none" }}
                        />
                        {ppSizePhotoPreview && (
                          <img
                            src={ppSizePhotoPreview}
                            alt="idFront"
                            style={{
                              maxWidth: "150px",
                              maxHeight: "70px",
                              marginLeft: "10px",
                            }}
                          />
                        )}
                      </label>
                    </FormControl>
                  </Grid>

                  {/* Upload Passport */}
                  <Grid item sm={12} md={6}>
                    <FormControl fullWidth>
                      <label
                        htmlFor="idFront"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <Button
                          variant="outlined"
                          color="info"
                          component="span"
                          size="large"
                          style={{
                            width: buttonWidth,
                            fontSize: "1rem",
                            textTransform: "none",
                          }}
                        >
                          Upload Passport
                        </Button>
                        <input
                          id="idFront"
                          name="idFront"
                          type="file"
                          onChange={handleIdFrontChange}
                          accept="image/*"
                          style={{ display: "none" }}
                        />
                        {idFrontPreview && (
                          <img
                            src={idFrontPreview}
                            alt="idFront"
                            style={{
                              maxWidth: "150px",
                              maxHeight: "70px",
                              marginLeft: "10px",
                            }}
                          />
                        )}
                      </label>
                    </FormControl>
                  </Grid>
                </>
              )}

              {idType === "pancard" && (
                <>
                  <Grid item sm={12} md={6}>
                    <TextField
                      label="Pancard Number"
                      name="idNumber"
                      type="text"
                      value={formData.idNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                    />
                  </Grid>

                  {/* PP-Size photo */}
                  <Grid item sm={12} md={6}>
                    <FormControl fullWidth>
                      <label
                        htmlFor="ppSizePhoto"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <Button
                          variant="outlined"
                          color="info"
                          component="span"
                          size="large"
                          style={{
                            width: buttonWidth,
                            fontSize: "1rem",
                            textTransform: "none",
                          }}
                        >
                          Upload pp-size photo
                        </Button>
                        <input
                          id="ppSizePhoto"
                          name="ppSizePhoto"
                          type="file"
                          onChange={handleppSizePhotoChange}
                          accept="image/*"
                          style={{ display: "none" }}
                        />
                        {ppSizePhotoPreview && (
                          <img
                            src={ppSizePhotoPreview}
                            alt="idFront"
                            style={{
                              maxWidth: "150px",
                              maxHeight: "70px",
                              marginLeft: "10px",
                            }}
                          />
                        )}
                      </label>
                    </FormControl>
                  </Grid>

                  {/* Upload Pancard */}
                  <Grid item sm={12} md={6}>
                    <FormControl fullWidth>
                      <label
                        htmlFor="idFront"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <Button
                          variant="outlined"
                          color="info"
                          component="span"
                          size="large"
                          style={{
                            width: buttonWidth,
                            fontSize: "1rem",
                            textTransform: "none",
                          }}
                        >
                          Upload Pancard
                        </Button>
                        <input
                          id="idFront"
                          name="idFront"
                          type="file"
                          onChange={handleIdFrontChange}
                          accept="image/*"
                          style={{ display: "none" }}
                        />
                        {idFrontPreview && (
                          <img
                            src={idFrontPreview}
                            alt="idFront"
                            style={{
                              maxWidth: "150px",
                              maxHeight: "70px",
                              marginLeft: "10px",
                            }}
                          />
                        )}
                      </label>
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>

            <Grid item xs={12} />
            <Grid container justifyContent="center" alignItems="center">
              <Button
                onSubmit={handleSubmit}
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                disabled={loading}
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        </FormControl>
      </form>
      {loading && <LoadingIndicator />}
    </Box>
  );
}

export default CreateUser;
